"use client";

import { c as _c } from "react/compiler-runtime";
import { faDownload, faExternalLink, faMaximize, faMinusSquare, faPlayCircle, faPlusSquare, faRefresh } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import { Col, Container, Dropdown, Row } from "react-bootstrap";
import { Tooltip } from "react-tooltip";
import { NextGenCollection, NextGenToken } from "@/entities/INextgen";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import Lightbulb from "./Lightbulb";
import styles from "./NextGenToken.module.scss";
import { NextGenTokenDownloadDropdownItem, Resolution } from "./NextGenTokenDownload";
import { NextGenTokenImage, get16KUrl, get8KUrl } from "./NextGenTokenImage";
import NextGenZoomableImage, { MAX_ZOOM_SCALE, MIN_ZOOM_SCALE } from "./NextGenZoomableImage";
interface Props {
  collection: NextGenCollection;
  token: NextGenToken;
}
enum Mode {
  LIVE = "Live",
  IMAGE = "Image",
  HIGH_RES = "High Res",
}
function NextGenTokenArtImage(props) {
  const $ = _c(10);
  if (props.mode === Mode.HIGH_RES) {
    let t0;
    if ($[0] !== props.is_fullscreen || $[1] !== props.onImageLoaded || $[2] !== props.setZoomScale || $[3] !== props.token || $[4] !== props.zoom_scale) {
      t0 = <NextGenZoomableImage token={props.token} is_fullscreen={props.is_fullscreen} zoom_scale={props.zoom_scale} setZoomScale={props.setZoomScale} maintain_aspect_ratio={false} onImageLoaded={props.onImageLoaded} />;
      $[0] = props.is_fullscreen;
      $[1] = props.onImageLoaded;
      $[2] = props.setZoomScale;
      $[3] = props.token;
      $[4] = props.zoom_scale;
      $[5] = t0;
    } else {
      t0 = $[5];
    }
    return t0;
  }
  const t0 = props.mode === Mode.LIVE;
  let t1;
  if ($[6] !== props.is_fullscreen || $[7] !== props.token || $[8] !== t0) {
    t1 = <NextGenTokenImage token={props.token} show_original={true} hide_info={true} hide_link={true} show_animation={t0} is_fullscreen={props.is_fullscreen} token_art={true} />;
    $[6] = props.is_fullscreen;
    $[7] = props.token;
    $[8] = t0;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  return t1;
}
export default function NextGenTokenArt(props) {
  const $ = _c(54);
  const isMobileDevice = useIsMobileDevice();
  const isMobileScreen = useIsMobileScreen();
  const [mode, setMode] = useState(Mode.IMAGE);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showBlackbox, setShowBlackbox] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const [zoomScale, setZoomScale] = useState(1);
  const [showZoomControls, setShowZoomControls] = useState(false);
  const tokenImageRef = useRef(null);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const handleKeyDown = event => {
        if (event.key === "Escape") {
          setShowLightbox(false);
          setShowBlackbox(false);
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      const handleClick = () => {
        setShowLightbox(false);
        setShowBlackbox(false);
      };
      window.addEventListener("mousedown", handleClick);
      const handleFullscreenChange = () => {
        if (document.fullscreenElement === tokenImageRef.current) {
          setIsFullScreen(true);
        } else {
          setIsFullScreen(false);
        }
      };
      window.addEventListener("fullscreenchange", handleFullscreenChange);
      return () => {
        window.removeEventListener("keydown", handleKeyDown);
        window.removeEventListener("fullscreenchange", handleFullscreenChange);
        window.removeEventListener("mousedown", handleClick);
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] !== mode) {
    t2 = function getModeStyle(m) {
      let s = `${styles.modeIcon}`;
      if (m === mode) {
        s = s + ` ${styles.modeIconSelected}`;
      }
      return s;
    };
    $[2] = mode;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const getModeStyle = t2;
  let t3;
  if ($[4] !== isMobileDevice || $[5] !== mode || $[6] !== props.token.animation_url || $[7] !== props.token.generator?.html || $[8] !== props.token.id || $[9] !== props.token.image_url) {
    t3 = function getCurrentHref() {
      if (mode === Mode.LIVE) {
        return props.token.animation_url ?? props.token.generator?.html;
      }
      if (mode === Mode.HIGH_RES) {
        if (isMobileDevice) {
          return get8KUrl(props.token.id);
        }
        return get16KUrl(props.token.id);
      }
      return props.token.image_url;
    };
    $[4] = isMobileDevice;
    $[5] = mode;
    $[6] = props.token.animation_url;
    $[7] = props.token.generator?.html;
    $[8] = props.token.id;
    $[9] = props.token.image_url;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const getCurrentHref = t3;
  let t4;
  if ($[11] !== zoomScale) {
    t4 = () => {
      setZoomScale(Math.max(zoomScale - 1, MIN_ZOOM_SCALE));
    };
    $[11] = zoomScale;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const handleScaleDown = t4;
  let t5;
  if ($[13] !== zoomScale) {
    t5 = () => {
      setZoomScale(Math.min(zoomScale + 1, MAX_ZOOM_SCALE));
    };
    $[13] = zoomScale;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const handleScaleUp = t5;
  let printModeIcons;
  if ($[15] !== getCurrentHref || $[16] !== getModeStyle || $[17] !== handleScaleDown || $[18] !== handleScaleUp || $[19] !== isMobileDevice || $[20] !== isMobileScreen || $[21] !== mode || $[22] !== props.token || $[23] !== showZoomControls || $[24] !== zoomScale) {
    printModeIcons = function printModeIcons() {
      return <><Col xs={12} sm={mode === Mode.HIGH_RES ? 4 : 6} className={`pt-2 pb-2 d-flex gap-3 align-items-center ${isMobileScreen ? "justify-content-center" : "justify-content-start"}`}><button className={`unselectable ${styles.imageResolutionBtn} ${mode === Mode.IMAGE ? styles.imageResolutionBtnSelected : ""}`} onClick={() => setMode(Mode.IMAGE)}>2K</button><button className={`unselectable ${styles.imageResolutionBtn} ${mode === Mode.HIGH_RES ? styles.imageResolutionBtnSelected : ""}`} onClick={() => setMode(Mode.HIGH_RES)}>{isMobileDevice ? "8K" : "16K"}</button><FontAwesomeIcon className={getModeStyle(Mode.LIVE)} onClick={() => setMode(Mode.LIVE)} icon={faPlayCircle} data-tooltip-id={`live-tooltip-${props.token.id}`} /><Tooltip id={`live-tooltip-${props.token.id}`} place="bottom" style={{
            backgroundColor: "#1F2937",
            color: "white",
            padding: "4px 8px"
          }}>Live</Tooltip></Col>{mode === Mode.HIGH_RES && <Col xs={6} sm={4} className="pt-2 pb-2 d-flex align-items-center gap-1 justify-content-center">{showZoomControls && <><FontAwesomeIcon className={styles.modeIcon} icon={faMinusSquare} onClick={handleScaleDown} style={{
              color: zoomScale === MIN_ZOOM_SCALE ? "#9a9a9a" : "white"
            }} /><span className="unselectable">Scale: {zoomScale}</span><FontAwesomeIcon className={styles.modeIcon} icon={faPlusSquare} onClick={handleScaleUp} style={{
              color: zoomScale === MAX_ZOOM_SCALE ? "#9a9a9a" : "white"
            }} /><FontAwesomeIcon className={styles.modeIcon} icon={faRefresh} onClick={() => setZoomScale(MIN_ZOOM_SCALE)} style={{
              paddingLeft: "5px",
              color: zoomScale === MIN_ZOOM_SCALE ? "#9a9a9a" : "white"
            }} /></>}</Col>}<Col xs={mode === Mode.HIGH_RES ? 6 : 12} sm={mode === Mode.HIGH_RES ? 4 : 6} className={`pt-2 pb-2 d-flex gap-3 align-items-center ${isMobileScreen ? "justify-content-center" : "justify-content-end"}`}><Lightbulb mode="black" className={styles.modeIcon} onClick={() => setShowBlackbox(true)} /><Lightbulb mode="light" className={styles.modeIcon} onClick={() => setShowLightbox(true)} /><Dropdown drop="down-centered" className="d-flex"><Dropdown.Toggle className={styles.downloadBtn}><FontAwesomeIcon className={styles.modeIcon} icon={faDownload} data-tooltip-id={`download-tooltip-${props.token.id}`} /><Tooltip id={`download-tooltip-${props.token.id}`} place="bottom" style={{
                backgroundColor: "#1F2937",
                color: "white",
                padding: "4px 8px"
              }}>Download</Tooltip></Dropdown.Toggle><Dropdown.Menu>{Object.values(Resolution).filter(_temp).map(resolution => <NextGenTokenDownloadDropdownItem resolution={resolution} token={props.token} key={resolution} />)}</Dropdown.Menu></Dropdown><FontAwesomeIcon className={styles.modeIcon} onClick={() => {
            const href = getCurrentHref();
            window.open(href, "_blank");
          }} icon={faExternalLink} data-tooltip-id={`external-tooltip-${props.token.id}`} /><Tooltip id={`external-tooltip-${props.token.id}`} place="bottom" style={{
            backgroundColor: "#1F2937",
            color: "white",
            padding: "4px 8px"
          }}>Open in new tab</Tooltip><FontAwesomeIcon className={styles.modeIcon} icon={faMaximize} onClick={toggleFullScreen} data-tooltip-id={`fullscreen-tooltip-${props.token.id}`} /><Tooltip id={`fullscreen-tooltip-${props.token.id}`} place="bottom" style={{
            backgroundColor: "#1F2937",
            color: "white",
            padding: "4px 8px"
          }}>Fullscreen</Tooltip></Col></>;
    };
    const toggleFullScreen = () => {
      if (tokenImageRef.current) {
        if (!document.fullscreenElement) {
          (tokenImageRef.current as any).requestFullscreen().catch(_temp2);
        } else {
          document.exitFullscreen?.();
        }
      }
    };
    $[15] = getCurrentHref;
    $[16] = getModeStyle;
    $[17] = handleScaleDown;
    $[18] = handleScaleUp;
    $[19] = isMobileDevice;
    $[20] = isMobileScreen;
    $[21] = mode;
    $[22] = props.token;
    $[23] = showZoomControls;
    $[24] = zoomScale;
    $[25] = printModeIcons;
  } else {
    printModeIcons = $[25];
  }
  let t6;
  if ($[26] !== showBlackbox || $[27] !== showLightbox) {
    t6 = function getBoxStyle() {
      if (showLightbox) {
        return styles.lightBox;
      }
      if (showBlackbox) {
        return styles.blackBox;
      }
      return `row ${styles.modeRow}`;
    };
    $[26] = showBlackbox;
    $[27] = showLightbox;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  const getBoxStyle = t6;
  let t7;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setShowZoomControls(false);
    };
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  let t8;
  if ($[30] !== mode) {
    t8 = [mode];
    $[30] = mode;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  useEffect(t7, t8);
  let t9;
  if ($[32] !== getBoxStyle) {
    t9 = getBoxStyle();
    $[32] = getBoxStyle;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  const t10 = showLightbox || showBlackbox ? styles.lightBoxContent : "col pt-3";
  let t11;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      setShowZoomControls(true);
    };
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== isFullScreen || $[36] !== mode || $[37] !== props.token || $[38] !== zoomScale) {
    t12 = <NextGenTokenArtImage token={props.token} mode={mode} is_fullscreen={isFullScreen} zoom_scale={zoomScale} setZoomScale={setZoomScale} onImageLoaded={t11} />;
    $[35] = isFullScreen;
    $[36] = mode;
    $[37] = props.token;
    $[38] = zoomScale;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== t10 || $[41] !== t12) {
    t13 = <div className={t10} ref={tokenImageRef}>{t12}</div>;
    $[40] = t10;
    $[41] = t12;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== t13 || $[44] !== t9) {
    t14 = <Row><Col><Container><Row><Col><div className={t9}>{t13}</div></Col></Row></Container></Col></Row>;
    $[43] = t13;
    $[44] = t9;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  let t15;
  if ($[46] !== printModeIcons) {
    t15 = <Row><Col><Container className={styles.modeRow}><Row className="pt-2 pb-1">{printModeIcons()}</Row></Container></Col></Row>;
    $[46] = printModeIcons;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== mode) {
    t16 = mode === Mode.LIVE && <Row className="pt-2 font-color-h font-smaller"><Col>* Live view generates the image dynamically from scratch in your browser. Pebbles have a computationally expensive script and the live view may take several minutes to render on your computer or phone. "Image view" will download faster.</Col></Row>;
    $[48] = mode;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] !== t14 || $[51] !== t15 || $[52] !== t16) {
    t17 = <Container>{t14}{t15}{t16}</Container>;
    $[50] = t14;
    $[51] = t15;
    $[52] = t16;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  return t17;
}
function _temp2(err) {
  alert(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
}
function _temp(r) {
  return ![Resolution["0.5K"], Resolution.Thumbnail].includes(r);
}